import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDate } from 'ns_libs/formatter';
import RenderIf from 'components/RenderIf/RenderIf';
import { faCalendarCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import DatePicker from 'react-datepicker';
import { DateField } from '../../../../constants';

type TimelineDatePickerProps = {
    value: Date | null;
    onChange: (date: Date | null) => void;
    field: DateField;
    isEditing: boolean;
    onEdit: () => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    placeholder?: string;
    disabled?: boolean;
};

const TimelineDatePicker = ({
    value,
    onChange,
    field,
    isEditing,
    onEdit,
    onKeyDown,
    placeholder = 'Select date',
    disabled = false,
}: TimelineDatePickerProps) => {
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const formatDateForDisplay = (date: Date | null): string => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleDateChange = (date: Date | null) => {
        onChange(date);
        setIsPickerOpen(false);
        inputRef.current?.focus();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === '') {
            onChange(null);
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' || e.key === 'Delete') {
            onChange(null);
            e.preventDefault();
        } else if (onKeyDown) {
            onKeyDown(e);
        }
    };

    const handleEdit = () => {
        if (!disabled) {
            onEdit();
            setIsPickerOpen(true);
        }
    };

    const closePicker = () => {
        setIsPickerOpen(false);
    };

    const noOp = () => {};

    if (isEditing) {
        return (
            <div style={{ position: 'relative' }} data-testid="timeline-date-picker-edit">
                <input
                    ref={inputRef}
                    type="text"
                    value={formatDateForDisplay(value)}
                    onChange={handleInputChange}
                    onClick={handleEdit}
                    onKeyDown={handleInputKeyDown}
                    className="form-control"
                    placeholder={placeholder}
                    disabled={disabled}
                />
                <RenderIf isTrue={isPickerOpen}>
                    <div className="TimelineTable__datepicker">
                        <DatePicker
                            selected={value}
                            onChange={handleDateChange}
                            inline
                            onClickOutside={closePicker}
                        />
                    </div>
                </RenderIf>
            </div>
        );
    }

    return (
        <div style={{ position: 'relative' }} data-testid="timeline-date-picker-view">
            <RenderIf isTrue={!!value}>
                <span
                    onClick={disabled ? noOp : handleEdit}
                    onKeyDown={disabled ? noOp : e => e.key === 'Enter' && handleEdit()}
                    role="button"
                    tabIndex={0}
                    className={disabled ? '' : 'text-primary cursor--pointer'}
                >
                    {value ? formatDate(value.toISOString()) : ''}
                </span>
            </RenderIf>
            <RenderIf isTrue={!value}>
                <FontAwesomeIcon
                    icon={faCalendarCirclePlus}
                    size="lg"
                    className={disabled ? '' : 'text-primary cursor--pointer'}
                    data-testid="calendar-icon"
                    aria-label={`Add ${field} date`}
                    onClick={disabled ? noOp : handleEdit}
                />
            </RenderIf>
            <RenderIf isTrue={isPickerOpen}>
                <div className="TimelineTable__datepicker">
                    <DatePicker
                        selected={value}
                        onChange={handleDateChange}
                        inline
                        onClickOutside={closePicker}
                    />
                </div>
            </RenderIf>
        </div>
    );
};

export default TimelineDatePicker;
